import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import { clearAllDrafts } from '../components/console/composerDraft'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Proactive-refresh tunables. Keep these here so the rationale is co-located.
//
// REFRESH_LEAD_FRACTION: refresh at this fraction of remaining lifetime.
//   With a 24h JWT, 0.5 = refresh at 12h. Lower = more refreshes (safer against
//   clock drift and missed timers); higher = fewer (cheaper). 0.5 is the
//   conservative-but-not-paranoid sweet spot.
// REFRESH_MIN_DELAY_MS: never schedule sooner than this. Stops a near-expiry
//   token from triggering a tight refresh loop.
// REFRESH_MAX_DELAY_MS: setTimeout > ~24.8 days overflows int32 in browsers
//   (clamps to 1ms and fires immediately). 23h is well below that for safety.
// FOCUS_REFRESH_THRESHOLD_MS: on tab-focus / visibility-restored, if the token
//   has less than this much life left (or is already past expiry but still
//   within the backend's 30-day grace), refresh immediately. Covers laptop
//   sleep/wake and long-backgrounded tabs where setTimeout fires unreliably.
const REFRESH_LEAD_FRACTION = 0.5
const REFRESH_MIN_DELAY_MS = 60_000              // 1 min
const REFRESH_MAX_DELAY_MS = 23 * 60 * 60 * 1000 // 23 h
const FOCUS_REFRESH_THRESHOLD_MS = 2 * 60 * 1000 // 2 min

/**
 * Decodes the `exp` claim (seconds since epoch) from a JWT without verifying
 * the signature. The frontend has no signing key, so we trust the server's
 * issuance and only use this for scheduling. Returns null on any parse failure.
 */
function decodeJwtExpSeconds(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(b64)
    const payload = JSON.parse(json) as { exp?: unknown }
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'agent' | 'business' | 'customer'
  tenantId?: string
  permissions: string[]
  preferences: {
    language: string
    timezone: string
    theme: 'dark' | 'light'
  }
}

/**
 * Membership shape returned by /api/auth/login when the typed (email, password) pair
 * matches SystemUser participants in 2+ tenants. Frontend renders a tenant picker;
 * user picks one; we call loginToTenant() to issue the JWT.
 *
 * Added 2026-05-02 (Warden v1.2 PR-9b — cross-tenant login).
 */
export interface TenantMembership {
  tenantId: string
  tenantName: string
  participantId: string
  globalParticipantId: string
  role: string
  displayName?: string | null
  lastLoginAt?: string | null
}

/**
 * Result of login(). Either:
 *   - { status: 'authenticated' } — the auth context is now signed in (single-tenant case)
 *   - { status: 'multi-tenant', memberships: [...] } — caller must render a picker
 *     and call loginToTenant() with the selected tenant
 */
export type LoginResult =
  | { status: 'authenticated' }
  | { status: 'multi-tenant'; memberships: TenantMembership[] }

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  loginToTenant: (email: string, password: string, tenantId: string) => Promise<void>
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  hasPermission: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // ────────────────────────────────────────────────────────────────────────────
  // Proactive token refresh
  //
  // Goal: keep an active session alive indefinitely. The backend issues 24h
  // JWTs and accepts refresh on tokens up to 30 days past expiry, so as long
  // as we refresh roughly every 12h while active (and on tab-focus after
  // sleep/wake), the user never gets bounced to login.
  //
  // Three triggers:
  //   1. setTimeout at ~50% of remaining lifetime (the happy path)
  //   2. visibilitychange/focus when token is near/past expiry (sleep/wake recovery)
  //   3. axios 401 interceptor in apiClient.ts (last-ditch reactive recovery)
  //
  // The refresh result is treated as authoritative — if the server rejects
  // (token too old, account locked, account gone), we clear local state and
  // bounce to login. If the network is just down, we leave state alone and
  // try again on the next trigger.
  // ────────────────────────────────────────────────────────────────────────────
  const refreshTimerRef = useRef<number | null>(null)
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null)

  const cancelProactiveRefresh = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  // Forward declaration — `scheduleProactiveRefresh` calls `refreshTokenNow`
  // which calls `scheduleProactiveRefresh`. We use refs so the closure can
  // see the latest version without restructuring as a useEffect dependency
  // chain. React renders this deterministically since we assign on every
  // render and read inside callbacks (not during render).
  const scheduleProactiveRefreshRef = useRef<((token: string) => void) | null>(null)

  const refreshTokenNow = useCallback(async (): Promise<boolean> => {
    // Dedupe: if a refresh is already in flight from any trigger (timer,
    // focus, axios interceptor), return its promise instead of starting a
    // second one. Multiple concurrent refreshes against the same token would
    // race; only the last `setItem` wins and earlier callers may end up with
    // a token that's already been replaced server-side.
    if (refreshInFlightRef.current) return refreshInFlightRef.current

    const token = localStorage.getItem('authToken')
    if (!token || token === 'demo-token') return false

    const promise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          // 401 means the token is past the 30-day refresh window or the
          // account was locked/deleted. Either way: not recoverable here.
          // Clear state so the user sees the login screen instead of a
          // half-broken authenticated UI making 401s on every API call.
          if (response.status === 401) {
            console.log('🔒 Refresh rejected by server — clearing session')
            localStorage.removeItem('authToken')
            setUser(null)
            setIsAuthenticated(false)
            cancelProactiveRefresh()
          } else {
            console.warn(`⚠️ Refresh failed with status ${response.status} — will retry on next trigger`)
          }
          return false
        }

        const data = await response.json() as { token?: string; user?: { id: string; name?: string; email?: string; role?: string; tenantId?: string } }
        if (!data.token) {
          console.warn('⚠️ Refresh response missing token field')
          return false
        }

        localStorage.setItem('authToken', data.token)

        // Update React state from the refresh response — the server is the
        // source of truth for role/permissions, so a server-side role change
        // takes effect on the next refresh without a re-login.
        if (data.user) {
          setUser({
            id: data.user.id,
            name: data.user.name || 'User',
            email: data.user.email || '',
            role: (data.user.role as User['role']) || 'customer',
            tenantId: data.user.tenantId,
            permissions: ['admin', 'super-admin'].includes(data.user.role || '') ? ['*'] : ['read'],
            preferences: {
              language: 'en',
              timezone: 'America/New_York',
              theme: 'dark'
            }
          })
          setIsAuthenticated(true)
        }

        // Re-arm the timer for the next refresh window.
        scheduleProactiveRefreshRef.current?.(data.token)
        console.log('✅ Token refreshed proactively')
        return true
      } catch (err) {
        // Network error / CORS / etc. — leave state alone, the next trigger
        // (focus event, axios 401) will retry.
        console.warn('⚠️ Refresh network error — will retry on next trigger:', err)
        return false
      } finally {
        refreshInFlightRef.current = null
      }
    })()

    refreshInFlightRef.current = promise
    return promise
  }, [cancelProactiveRefresh])

  const scheduleProactiveRefresh = useCallback((token: string) => {
    cancelProactiveRefresh()
    const exp = decodeJwtExpSeconds(token)
    if (exp === null) {
      // Token has no exp claim — can't schedule. Log once and bail; reactive
      // 401 interceptor will still catch eventual expiry.
      console.warn('⚠️ Token has no exp claim; proactive refresh disabled')
      return
    }
    const remainingMs = exp * 1000 - Date.now()
    if (remainingMs <= 0) {
      // Already expired — refresh immediately rather than scheduling.
      void refreshTokenNow()
      return
    }
    const delayMs = Math.max(
      REFRESH_MIN_DELAY_MS,
      Math.min(remainingMs * REFRESH_LEAD_FRACTION, REFRESH_MAX_DELAY_MS)
    )
    refreshTimerRef.current = window.setTimeout(() => {
      void refreshTokenNow()
    }, delayMs)
    console.log(`🔁 Proactive token refresh scheduled in ${(delayMs / 60_000).toFixed(1)} min (token expires in ${(remainingMs / 60_000).toFixed(1)} min)`)
  }, [cancelProactiveRefresh, refreshTokenNow])

  // Keep the ref pointing at the latest closure so refreshTokenNow can call
  // back into scheduleProactiveRefresh without a circular dep.
  scheduleProactiveRefreshRef.current = scheduleProactiveRefresh

  // Recover from sleep/wake and long-backgrounded tabs. setTimeout is unreliable
  // when the tab is hidden — many browsers throttle it to 1Hz and some pause
  // it entirely on system sleep — so we also check on visibility-restored and
  // focus events. If the token is already near/past expiry, refresh now.
  useEffect(() => {
    const onMaybeRefresh = () => {
      if (document.hidden) return
      const token = localStorage.getItem('authToken')
      if (!token || token === 'demo-token') return
      const exp = decodeJwtExpSeconds(token)
      if (exp === null) return
      const remainingMs = exp * 1000 - Date.now()
      if (remainingMs < FOCUS_REFRESH_THRESHOLD_MS) {
        console.log(`🔁 Tab focused — token has ${(remainingMs / 1000).toFixed(0)}s left, refreshing now`)
        void refreshTokenNow()
      }
    }
    document.addEventListener('visibilitychange', onMaybeRefresh)
    window.addEventListener('focus', onMaybeRefresh)
    return () => {
      document.removeEventListener('visibilitychange', onMaybeRefresh)
      window.removeEventListener('focus', onMaybeRefresh)
    }
  }, [refreshTokenNow])

  // Cancel any pending refresh on provider unmount.
  useEffect(() => {
    return () => {
      cancelProactiveRefresh()
    }
  }, [cancelProactiveRefresh])

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        if (token && token !== 'demo-token') {
          // Validate token with backend first
          try {
            const response = await fetch(`${API_BASE}/api/auth/validate`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })

            if (response.ok) {
              const data = await response.json()
              if (data.valid && data.user) {
                setIsAuthenticated(true)
                setUser({
                  id: data.user.id,
                  name: data.user.name || 'User',
                  email: data.user.email || '',
                  role: data.user.role || 'customer',
                  tenantId: data.user.tenantId,
                  permissions: ['admin', 'super-admin'].includes(data.user.role) ? ['*'] : ['read'],
                  preferences: {
                    language: 'en',
                    timezone: 'America/New_York',
                    theme: 'dark'
                  }
                })
                console.log('✅ Token validated successfully with backend, role:', data.user.role)
                scheduleProactiveRefresh(token)
                return // Exit early on successful validation
              } else {
                console.log('❌ Token marked invalid by backend, clearing auth')
                localStorage.removeItem('authToken')
                setUser(null)
                setIsAuthenticated(false)
                return
              }
            } else if (response.status === 401) {
              // Token expired or invalid - try to refresh it
              console.log('🔄 Token rejected (401), attempting refresh...')

              try {
                const refreshResponse = await fetch(`${API_BASE}/api/auth/refresh`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                })

                if (refreshResponse.ok) {
                  const refreshData = await refreshResponse.json()
                  if (refreshData.token && refreshData.user) {
                    // Save new token and set user
                    localStorage.setItem('authToken', refreshData.token)
                    setIsAuthenticated(true)
                    setUser({
                      id: refreshData.user.id,
                      name: refreshData.user.name || 'User',
                      email: refreshData.user.email || '',
                      role: refreshData.user.role || 'customer',
                      tenantId: refreshData.user.tenantId,
                      permissions: ['admin', 'super-admin'].includes(refreshData.user.role) ? ['*'] : ['read'],
                      preferences: {
                        language: 'en',
                        timezone: 'America/New_York',
                        theme: 'dark'
                      }
                    })
                    console.log('✅ Token refreshed successfully, role:', refreshData.user.role)
                    scheduleProactiveRefresh(refreshData.token)
                    return
                  }
                }

                // Refresh failed
                console.log('❌ Token refresh failed, clearing auth')
                localStorage.removeItem('authToken')
                setUser(null)
                setIsAuthenticated(false)
                return
              } catch (refreshErr) {
                console.error('❌ Token refresh error:', refreshErr)
                localStorage.removeItem('authToken')
                setUser(null)
                setIsAuthenticated(false)
                return
              }
            } else {
              // For other errors (500, network issues), authentication fails
              console.error(`❌ Backend validation failed with status ${response.status}`)
              localStorage.removeItem('authToken')
              setUser(null)
              setIsAuthenticated(false)
              return
            }
          } catch (err: unknown) {
            // Network error or backend unavailable - no fallback, require backend auth
            const errorMessage = err instanceof Error ? err.message : 'Unknown error'
            console.error('❌ Failed to reach backend for token validation:', errorMessage)
            localStorage.removeItem('authToken')
            setUser(null)
            setIsAuthenticated(false)
          }
        } else {
          // No token or old demo token
          if (token === 'demo-token') {
            localStorage.removeItem('authToken')
          }
          setUser(null)
          setIsAuthenticated(false)
        }
      } catch (error) {
        console.error('❌ Auth initialization failed:', error)
        setUser(null)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()
  }, [])

  /**
   * Apply a successful auth response (token + user payload) to context state. Shared
   * between single-tenant login and stage-2 login-to-tenant.
   */
  const applyAuthSuccess = (token: string, userPayload: { id: string; name?: string; email?: string; role?: string; tenantId?: string }) => {
    localStorage.setItem('authToken', token)
    const userData: User = {
      id: userPayload.id,
      name: userPayload.name || 'User',
      email: userPayload.email || '',
      role: (userPayload.role as User['role']) || 'customer',
      tenantId: userPayload.tenantId,
      permissions: ['admin', 'super-admin'].includes(userPayload.role || '') ? ['*'] : ['read'],
      preferences: {
        language: 'en',
        timezone: 'America/New_York',
        theme: 'dark'
      }
    }
    setUser(userData)
    setIsAuthenticated(true)
    scheduleProactiveRefresh(token)
  }

  const login = async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true)
    console.log('Attempting login with:', email)

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: email, password }),
      })

      console.log('✅ Login response status:', response.status)

      if (!response.ok) {
        // Try to parse server error message for the toast
        let serverMessage: string | undefined
        try {
          const errBody = await response.json() as { message?: string }
          serverMessage = errBody?.message
        } catch { /* not JSON, fall back to status text */ }
        console.error('❌ Login failed with status:', response.status, serverMessage)
        throw new Error(serverMessage || `Login failed: ${response.status}`)
      }

      const data = await response.json() as
        | { token: string; user: { id: string; name?: string; email?: string; role?: string; tenantId?: string } }
        | { requiresTenantSelection: true; memberships: TenantMembership[] }

      // PR-9b multi-tenant case — caller must render a picker and follow up with loginToTenant.
      if ('requiresTenantSelection' in data && data.requiresTenantSelection) {
        console.log(`🌐 Multi-tenant login: ${data.memberships.length} memberships available`)
        return { status: 'multi-tenant', memberships: data.memberships }
      }

      // Single-tenant case — same shape as before.
      if (!('token' in data) || !data.token) {
        throw new Error('No token received from login response')
      }
      applyAuthSuccess(data.token, data.user)
      console.log('✅ User authenticated:', data.user.email, data.user.role)
      return { status: 'authenticated' }
    } catch (error) {
      console.error('Login error:', error)
      throw error // Propagate error - no fallback
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * PR-9b stage-2: after the picker, call this with the chosen tenantId. The server
   * re-validates the password against that tenant's specific Participant and issues
   * a JWT scoped to it.
   */
  const loginToTenant = async (email: string, password: string, tenantId: string): Promise<void> => {
    setIsLoading(true)
    console.log(`🎯 Login-to-tenant: ${email} → ${tenantId}`)
    try {
      const response = await fetch(`${API_BASE}/api/auth/login-to-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: email, password, tenantId }),
      })

      if (!response.ok) {
        let serverMessage: string | undefined
        try {
          const errBody = await response.json() as { message?: string }
          serverMessage = errBody?.message
        } catch { /* */ }
        throw new Error(serverMessage || `Login failed: ${response.status}`)
      }

      const data = await response.json() as { token: string; user: { id: string; name?: string; email?: string; role?: string; tenantId?: string } }
      if (!data.token) throw new Error('No token received from login-to-tenant response')

      applyAuthSuccess(data.token, data.user)
      console.log('✅ Stage-2 authenticated for tenant', tenantId)
    } catch (error) {
      console.error('Login-to-tenant error:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    console.log('🚪 Logging out user')
    cancelProactiveRefresh()
    setUser(null)
    setIsAuthenticated(false)
    localStorage.removeItem('authToken')
    clearAllDrafts() // the composer drafts are this operator's, not the next login's (composerDraft.ts)
    console.log('✅ User logged out and token cleared')
  }

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates })
    }
  }

  const hasPermission = (permission: string): boolean => {
    if (!user) return false
    return user.permissions.includes('*') || user.permissions.includes(permission)
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginToTenant,
    logout,
    updateUser,
    hasPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
